import React from 'react';
import styled from 'styled-components';

import {Row, Col } from 'reactstrap';

import { VerticalNumberAndLabel, ChargeDelayControl, IndividualTreatmentVisualisationStandard, MagstimColor, CurrentLang, CurrentTheme, isPDA } from "common";
import TabBase from 'common/controls/TabBase';

import * as ControlConstants from 'common/controls/Magstim.Control.Constants';
import * as StringUtils from 'common/utils/StringUtils.js'



class TabStandard extends TabBase {

	constructor() {
		super();

		this.state = {
			inputGroupWidth: isPDA() ?  '100px' : '150px',
			leftColumOffset: isPDA() ?  { size: 10, offset: 0 } : { size: 8, offset: 1 },
			rightColumOffset: isPDA() ? { size: 2, offset: 0 } : { size: 3, offset: 0 } 
		}

		this._elemOnTime = React.createRef();
		this._elemOffTime = React.createRef();
		this._elemPulsesPerTrain = React.createRef();
		this._elemTotalTrains = React.createRef();

		this._pulsesPerSecond = ControlConstants.UI_STANDARD_PULSES_PER_SEC.default;
		this._pulsesPerTrain = ControlConstants.UI_STANDARD_PULSES_PER_TRAIN.default;
		this._onTime = ControlConstants.UI_STANDARD_ON_TIME.default;
		this._offTime = ControlConstants.UI_STANDARD_OFF_TIME.default;
		this._adjustedPowerOutput = ControlConstants.UI_STANDARD_PC_MT.default;
		this._totalTrains = ControlConstants.UI_STANDARD_TOTAL_TRAINS.default;
		this._chargeDelay = ControlConstants.UI_STANDARD_CHARGE_DELAY.default;
	}

	values() {
		var retVal = super.values();
		retVal.pulsesPerTrain = this._pulsesPerTrain;
		retVal.onTime = this._onTime;
		retVal.totalTrains = this._totalTrains;
		retVal.offTime = this._offTime;
		return retVal;
	}

	setValues(values) {

		//console.log('setValues', values);
		
		super.setValues(values);

		if (undefined !== values.pulsesPerTrainHighlight) { this._elemPulsesPerTrain.current.setHighlight(values.pulsesPerTrainHighlight); }
		if (undefined !== values.onTimeHighlight) { this._elemOnTime.current.setHighlight(values.onTimeHighlight); }
		if (undefined !== values.totalTrainsHighlight) { this._elemTotalTrains.current.setHighlight(values.totalTrainsHighlight); }
		if (undefined !== values.offTimeHighlight) { this._elemOffTime.current.setHighlight(values.offTimeHighlight); }

		if (undefined !== values.pulsesPerTrain) { this._elemPulsesPerTrain.current.setValue(values.pulsesPerTrain); this.setPulsesPerTrain(values.pulsesPerTrain); }
		if (undefined !== values.onTime) { this._elemOnTime.current.setValue(values.onTime); this.setOnTime(values.onTime); } 
		if (undefined !== values.totalTrains) { this._elemTotalTrains.current.setValue(values.totalTrains); this.setTotalTrains(values.totalTrains); }
		if (undefined !== values.offTime) { this._elemOffTime.current.setValue(values.offTime); this.setOffTime(values.offTime); }

		this.updateTotalDuration();
		this.updateTotalPulses();
		this.updateOnTime();

		// Clear after load
		this.setNonFormikInputsTouched(false);
	}

	setPulsesPerTrain(value) {
		// console.log(`TabStandard.setPulsesPerTrain ${this._pulsesPerTrain} --> ${value}`);
		this._elemVisualisation.current.setPulsesPerTrain(value);
		this._pulsesPerTrain = value;

		this.setNonFormikInputsTouched(true);
	}
	onChangePulsesPerTrain = (value) => {
		// console.log(`TabStandard.onChangePulsesPerTrain ${this._pulsesPerTrain} --> ${value}`);
		const prevValue = this._pulsesPerTrain;

		this.setPulsesPerTrain(value);
		this.updateTotalPulses(); // onChangePulsesPerTrain
		this.updateOnTime();
		this.updateTotalDuration();

		if (this.calculateTotalPulses() > ControlConstants.TOTAL_PULSES) {
			console.log(`${this.calculateTotalPulses()} pulses exceeds max (${ControlConstants.TOTAL_PULSES}) resetting PulsesPerTrain back to ${prevValue} from ${value}`);
			this._elemPulsesPerTrain.current.setValue(prevValue);
			this.onChangePulsesPerTrain(prevValue);
		}
	}

	setVisualisationCycleTime() {
		// this.logValues('setVisualisationCycleTime PRE ');
		// console.log(`TabStandard.setVisualisationCycleTime() _onTime=${this._onTime} _offTime=${this._offTime}`);
		this._elemVisualisation.current.setOnTime(Number(this._onTime) + Number(this._offTime));
		// this.logValues('setVisualisationCycleTime POST');
	}

	setOnTime(value) {
		// console.log(`TabStandard.setOnTime ${this._onTime} --> ${value}`);
		this._onTime = value;
		this.setVisualisationCycleTime();

		this.setNonFormikInputsTouched(true);
	}

	onChangeOnTime = (value) => {
		const prevValue = this._onTime;

		// console.log(`TabStandard.onChangeOnTime ${this._onTime} --> ${value}`);
	
		const oldStep = this.calculateOnTimeStep(this._onTime);
		const step = this.calculateOnTimeStep(value);

		this.setOnTime(value);

		this.updateTotalDuration();
		this.updatePulsesPerTrain(); // onChangeOnTime
		
		if (oldStep !== step) {
			// console.log(`TabStandard.onChangeOnTime step : ${oldStep} --> ${step}`);
			this._elemOnTime.current.setStep(step);
		}

		this.updateOnTimeMax();
		
		if (this.calculateTotalPulses() > ControlConstants.TOTAL_PULSES) {
			console.log(`${this.calculateTotalPulses()} pulses exceeds max (${ControlConstants.TOTAL_PULSES}) resetting OnTime back to ${prevValue} from ${value}`);
			this._elemOnTime.current.setValue(prevValue, false);
			this.onChangeOnTime(prevValue);
		}
	
	}

	updatePulsesPerTrain() {
		var calcValue = Number(Number(this._onTime) * Number(this._pulsesPerSecond)).toFixed(0);
		// console.log(`TabStandard.updatePulsesPerTrain _pulsesPerTrain=${this._pulsesPerTrain} _pulsesPerSecond=${this._pulsesPerSecond} _onTime=${this._onTime} calcValue=${calcValue}`);
		if (calcValue !== this._pulsesPerTrain) {
			this._elemPulsesPerTrain.current.setValue(calcValue);
			this.setPulsesPerTrain(calcValue);
			this.updateTotalPulses(); // updatePulsesPerTrain
		}
	}

	updateOnTimeMax() {

		const ontime_calc_max = ControlConstants.TOTAL_PULSES / this._totalTrains / this._pulsesPerSecond;
		if (ontime_calc_max < ControlConstants.UI_STANDARD_ON_TIME.max) {
			this._elemOnTime.current.setMax(ontime_calc_max);
		}
		else {
			this._elemOnTime.current.setMax(ControlConstants.UI_STANDARD_ON_TIME.max);
		}
		//console.log(`ontime_calc_max=${ontime_calc_max}`);

	}
	updateOnTime() {
		var calcValue = Number(Number(this._pulsesPerTrain) / Number(this._pulsesPerSecond)).toFixed(1);
		// console.log(`TabStandard.updateOnTime _pulsesPerTrain=${this._pulsesPerTrain} _pulsesPerSecond=${this._pulsesPerSecond} _onTime=${this._onTime} calcValue=${calcValue}`);
		// this.logValues('updateOnTime PRE ');
		if (calcValue !== this._onTime) {
			this._elemOnTime.current.setValue(calcValue);
			this.setOnTime(calcValue);
			this.updateTotalDuration();
		}

		this.updateOnTimeMax();
		// this.logValues('updateOnTime POST');
	}

	logValues(prefix) {
		var str = '\n';
		str += `[${prefix}] pulsesPerSecond     = ${this._pulsesPerSecond} [${this._elemPulsesPerSecond.current.getValue()}]\n`
		str += `[${prefix}] pulsesPerTrain      = ${this._pulsesPerTrain} [${this._elemPulsesPerTrain.current.getValue()}]\n`
		str += `[${prefix}] onTime              = ${this._onTime} [${this._elemOnTime.current.getValue()}]\n`
		str += `[${prefix}] offTime             = ${this._offTime} [${this._elemOffTime.current.getValue()}]\n`
		str += `[${prefix}] adjustedPowerOutput = ${this._adjustedPowerOutput} [${this._elemAdjustedPowerOutput.current.getValue()}]\n`
		str += `[${prefix}] totalTrains         = ${this._totalTrains} [${this._elemTotalTrains.current.getValue()}]\n`
		str += `[${prefix}] chargeDelay         = ${this._chargeDelay} [${this._elemChargeDelay.current.getValue()}]\n`
		console.log(str);
	}

	setOffTime(value) {
		this._offTime = value;
		this._elemVisualisation.current.setOffTime(value);
		this.setVisualisationCycleTime();

		this.setNonFormikInputsTouched(true);
	}
	onChangeOffTime = (value) => {
		this.setOffTime(value);
		this.updateTotalDuration();
	}

	onChangePulsesPerSecond = (value) => {
		const prevValue = this._pulsesPerSecond;

		this.setPulsesPerSecond(value);
		this.updatePulsesPerTrain(); // onChangePulsesPerSecond

		if (this.calculateTotalPulses() > ControlConstants.TOTAL_PULSES) {
			console.log(`${this.calculateTotalPulses()} pulses exceeds max (${ControlConstants.TOTAL_PULSES}) resetting PulsesPerSecond back to ${prevValue} from ${value}`);
			this._elemPulsesPerSecond.current.setValue(prevValue);
			this.onChangePulsesPerSecond(prevValue);
		}
	
	}

	setTotalTrains(value) {
		this._totalTrains = value;

		this.setNonFormikInputsTouched(true);
	}
	onChangeTotalTrains = (value) => {
		const prevValue = this._totalTrains;

		this.setTotalTrains(value);
		this.updateTotalPulses(); // onChangeTotalTrains
		this.updateTotalDuration();

		if (this.calculateTotalPulses() > ControlConstants.TOTAL_PULSES) {
			console.log(`${this.calculateTotalPulses()} pulses exceeds max (${ControlConstants.TOTAL_PULSES}) resetting TotalTrains back to ${prevValue} from ${value}`);
			this._elemTotalTrains.current.setValue(prevValue);
			this.onChangeTotalTrains(prevValue);
		}
	
	}

	calculateTotalDuration() {
		return Number(this._totalTrains) * (Number(this._onTime) + Number(this._offTime));
	}

	calculateTotalPulses() {
		return Number(this._totalTrains) * Number(this._pulsesPerTrain);
	}

	calculatePulsesPerSecondStep(value) {
		if (value < 30) {
			return 0.1;
		}
		else {
			return 1.0;
		}
	}

	calculateOnTimeStep(value) {
		if (value < 30) {
			// console.log(`calculateOnTimeStep(${value}) returning 0.1`);
			return 0.1;
		}
		else {
			// console.log(`calculateOnTimeStep(${value}) returning 1.0`);
			return 1.0;
		}
	}

	render() {

		// console.log(`render _chargeDelay=${this._chargeDelay}`);

		if (!this.props.displayMT) {
			this._powerOutput = null;
		}
		
		return (
			<Wrapper inputGroupWidth={this.state.inputGroupWidth}>
				<Row >

					<Col md={this.state.leftColumOffset} className="border-right">

						<Row className="ValueEntryRow">
							<VerticalNumberAndLabel id='idAdjustedPowerOutput' ref={this._elemAdjustedPowerOutput} onChange={this.onChangeAdjustedPowerOutput} text={CurrentLang().view_individual_treatment_standard_percentage_of_rmt}
								min={ControlConstants.UI_STANDARD_PC_MT.min}
								max={ControlConstants.UI_STANDARD_PC_MT.max}
								step={ControlConstants.UI_STANDARD_PC_MT.step}
								defaultValue={this._adjustedPowerOutput}
								format={StringUtils.formatPercent}
								size={CurrentTheme().ControlSize}
							/>
							<VerticalNumberAndLabel id='idPulsesPerSecond' ref={this._elemPulsesPerSecond} onChange={this.onChangePulsesPerSecond} text={CurrentLang().view_individual_treatment_standard_pulses_per_second}
								min={ControlConstants.UI_STANDARD_PULSES_PER_SEC.min}
								max={ControlConstants.UI_STANDARD_PULSES_PER_SEC.max}
								defaultValue={this._pulsesPerSecond}
								step={this.calculatePulsesPerSecondStep(this._pulsesPerSecond)}
								size={CurrentTheme().ControlSize}
								/>
							<VerticalNumberAndLabel id='idPulsesPerTrain' ref={this._elemPulsesPerTrain} onChange={this.onChangePulsesPerTrain} text={CurrentLang().view_individual_treatment_standard_pulses_per_train}
								defaultValue={this._pulsesPerTrain}
								min={ControlConstants.UI_STANDARD_PULSES_PER_TRAIN.min}
								max={ControlConstants.UI_STANDARD_PULSES_PER_TRAIN.max}
								step={ControlConstants.UI_STANDARD_PULSES_PER_TRAIN.step}
								size={CurrentTheme().ControlSize}
							/>
							<VerticalNumberAndLabel id='idOnTime' ref={this._elemOnTime} onChange={this.onChangeOnTime} text={CurrentLang().view_individual_treatment_standard_on_time}
								min={ControlConstants.UI_STANDARD_ON_TIME.min}
								max={ControlConstants.UI_STANDARD_ON_TIME.max}
								defaultValue={Number(this._onTime)}
								step={this.calculateOnTimeStep(this._onTime)}
								format={StringUtils.formatSeconds}
								size={CurrentTheme().ControlSize}
							/>
							<VerticalNumberAndLabel id='idOffTime' ref={this._elemOffTime} onChange={this.onChangeOffTime} text={CurrentLang().view_individual_treatment_standard_off_time}
								min={ControlConstants.UI_STANDARD_OFF_TIME.min}
								max={ControlConstants.UI_STANDARD_OFF_TIME.max}
								defaultValue={this._offTime}
								step={ControlConstants.UI_STANDARD_OFF_TIME.step}
								format={StringUtils.formatSeconds}
								size={CurrentTheme().ControlSize}
							/>
						</Row>

						<Row className="delayRow">
							<Col md={3} className="ml-2 mr-2">
								<ChargeDelayControl ref={this._elemChargeDelay}
									onChangeDuration={this.onChangeChargeDelay}
									onChangeIsEnabled={this.onChangeChargeDelayIsEnabled}
									defaultValue={this._chargeDelay}
									min={ControlConstants.UI_STANDARD_CHARGE_DELAY.min}
									max={ControlConstants.UI_STANDARD_CHARGE_DELAY.max}
									step={ControlConstants.UI_STANDARD_CHARGE_DELAY.step}
									isEnabled={false}
									id="idChargeDelayControlStandard"
								/>
							</Col>

							<Col className="data-chart">
								<IndividualTreatmentVisualisationStandard
									ref={this._elemVisualisation}
									AdjustedPowerOutput={this._adjustedPowerOutput}
									PowerOutput={this._powerOutput}
									ChargeDelay={null}
									PulsesPerTrain={this._pulsesPerTrain}
									OnTime={Number(this._onTime + this._offTime)}
									OffTime={this._offTime}
								/>
							</Col>
						</Row>

					</Col>

					<Col md={this.state.rightColumOffset} className="totalValues">
						<div>
						<Row className="ValueEntryRow">
							<VerticalNumberAndLabel id='idTotalTrains' ref={this._elemTotalTrains}
								onChange={this.onChangeTotalTrains}
								defaultValue={this._totalTrains}
								text={CurrentLang().view_individual_treatment_standard_total_trains}
								min={ControlConstants.UI_STANDARD_TOTAL_TRAINS.min}
								max={ControlConstants.UI_STANDARD_TOTAL_TRAINS.max}
								step={ControlConstants.UI_STANDARD_TOTAL_TRAINS.step}
								size={CurrentTheme().ControlSize}
							/>
						</Row>
						<Row className="ValueEntryRow">
							<VerticalNumberAndLabel id='idTotalPulses' ref={this._elemTotalPulses} defaultValue={this.calculateTotalPulses()} text={CurrentLang().view_individual_treatment_standard_total_pulses} readonly={true} size={CurrentTheme().ControlSize} />
						</Row>
						<Row className="ValueEntryRow">
							<VerticalNumberAndLabel id='idTotalDurationStandard' ref={this._elemTotalDuration} defaultValue={this.calculateTotalDuration()} text={CurrentLang().view_individual_treatment_standard_total_duration} readonly={true} format={StringUtils.formatMinuteAndSeconds} size={CurrentTheme().ControlSize} />
						</Row>
						</div>
					</Col>
				</Row>

			</Wrapper>
		);
	}
}

const Wrapper = styled.section`
	margin: 0;
	padding: 0;
	background: ${ MagstimColor.MagstimGroupBackground};
	color: ${ MagstimColor.MagstimWhite};

	.ValueEntryRow {
		margin: 0
		padding-top: 30px;
		padding-bottom: 30px;

		input {
			color: ${ MagstimColor.MagstimWhite};
			font-weight: bold;
			width:  ${props => props.inputGroupWidth};
			margin-left: auto;
			margin-right: auto;
			text-align: center

			&:focus {
				color: ${ MagstimColor.MagstimPrimaryBlue};
			}
		}

		.col {
			vertical-align: bottom;
			align-self: flex-end;
		}
	}
	.totalValues .ValueEntryRow {
		padding-bottom: 25px;
	}
	.delayRow {
		padding-bottom: 30px;
	}
	.data-chart {
		padding: 20px 30px 20px 0 ;
	}
`;

export default TabStandard;